import { motion } from "framer-motion";
import { AnimatedSection, NoiseTexture } from "../shared";

const ROADMAP = [
  {
    title: "Early Access & Onboarding",
    status: "done",
    date: "Completed",
    bullets: ["Gamified onboarding for true degens", "OG NFTs for early users"],
  },
  {
    title: "AGNT Connect Launch",
    status: "current",
    date: "Current",
    bullets: [
      "Buy, send, receive, and swap tokens directly inside X",
      "Project insights including follower data, token scores, and more",
      "SocialFi Campaigns for projects directly in X Feed with offchain actions",
    ],
  },
  {
    title: "Hub Foundation",
    status: "upcoming",
    date: "Q2 2026 · Part I",
    bullets: [
      "Hub dashboard v1 inside AGNT Connect: account system, home screen, agent management panel",
      "Auto-provisioned personal server on sign-up: no Docker, no CLI, no config",
      "Core agent framework: standard SDK for building, deploying, and managing agent blocks",
      "Wallet connection + multi-chain support (BNB Chain, Ethereum, Solana, Base)",
    ],
  },
  {
    title: "First Agent Blocks",
    status: "upcoming",
    date: "Q2 2026 · Part II",
    bullets: [
      "Portfolio Tracker agent: cross-chain balance, PnL, alerts, exportable reports",
      "Smart Alerts agent: custom triggers for price, whale moves, launches, governance votes",
      "Reply Guy agent: AI-powered X engagement with tone control, target lists, and approval queue",
      "Agent Library UI: browse, activate, deactivate, configure agents from the dashboard",
      '"Request an Agent" pipeline: community submits demand, we prioritize and ship',
    ],
  },
  {
    title: "DeFi Agents + Neobank",
    status: "upcoming",
    date: "Q3 2026",
    bullets: [
      "DeFi Yield Optimizer: scans protocols, auto-rebalances capital based on risk profile",
      "Prediction Market Arb Bot: monitors mispriced outcomes, auto-executes when edge is found",
      "Neo3 Bank & Cards: crypto-linked card, on/off-ramp, agent-linked settlements",
      "$ATTS payment integration: subscriptions, agent fees, and neobank transactions settle in $ATTS",
    ],
  },
  {
    title: "Advanced Intelligence + Enterprise",
    status: "upcoming",
    date: "H1 2027",
    bullets: [
      "Multi-agent workflows: chain agents together with conditional logic",
      "Agent memory + learning: persistent context, adaptive execution, cross-agent data sharing",
      "Enterprise / Team hubs: multi-user access, roles, shared configs, team analytics",
      "Public API + developer platform: webhooks, SDKs, usage-based access via $ATTS",
    ],
  },
  {
    title: "Multi-Platform + Ecosystem",
    status: "upcoming",
    date: "Q4 2027",
    bullets: [
      "AGNT Desktop app (Win/Mac): full hub with system notifications and background agent execution",
      "AGNT Mobile app (iOS/Android): agent monitoring, push alerts, neobank on the go",
      "Agent Marketplace: third-party developers build and publish agents, revenue share in $ATTS",
      "$ATTS staking + governance: lock tokens for premium benefits, vote on platform priorities",
      "Cross-chain agent execution: agents operate across multiple chains in a single workflow",
    ],
  },
];

export default function NewRoadmap() {
  return (
    <section className="py-12 md:py-16 px-6 relative overflow-hidden">
      <NoiseTexture />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0c0c0c] to-[#0a0a0a]" />

      <div className="max-w-4xl mx-auto relative z-10">
        <AnimatedSection>
          <div className="mb-12 text-center">
            <span className="inline-flex items-center gap-2.5 text-[11px] font-semibold tracking-[0.2em] uppercase text-white/35 mb-5 before:content-[''] before:w-5 before:h-px before:bg-gradient-to-r before:from-violet-500/60 before:to-transparent">
              See what's next in the AGNT.Hub journey
            </span>
            <h2 className="font-space-grotesk text-4xl md:text-6xl font-bold tracking-[-0.03em] gradient-text-bright">
              Roadmap
            </h2>
          </div>
        </AnimatedSection>

        <div className="relative pl-10 ml-2 md:ml-8 flex flex-col gap-12">
          <div className="absolute left-0 top-[2px] bottom-0 w-px bg-gradient-to-b from-white/[0.15] via-white/[0.06] to-transparent" />

          {ROADMAP.map((item, i) => (
            <motion.div
              key={i}
              className="relative"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
            >
              <motion.div
                className={`absolute -left-[45.5px] top-[2px] backdrop-blur-md w-3 h-3 rounded-full ${
                  item.status === "upcoming" ? "bg-white/20" : "bg-white"
                }`}
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 + i * 0.06, type: "spring" }}
              />
              {item.status === "current" && (
                <motion.div
                  className="absolute -left-[52px] -top-[4.5px] size-[25px] rounded-full border border-white/20"
                  animate={{ scale: [1, 1.6, 1], opacity: [0.4, 0, 0.4] }}
                  transition={{ duration: 2.5, repeat: Infinity }}
                />
              )}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-3">
                  <span
                    className={`text-[10px] font-bold uppercase tracking-[0.15em] ${
                      item.status === "current"
                        ? "text-white/70"
                        : "text-white/50"
                    }`}
                  >
                    {item.date}
                  </span>
                  {item.status === "current" && (
                    <span className="text-[10px]  font-bold px-2.5 py-0.5 bg-white/10 text-white/60 rounded-full border border-white/[0.06]">
                      Active
                    </span>
                  )}
                </div>
                <h3 className="font-space-grotesk text-xl md:text-2xl font-bold text-white">
                  {item.title}
                </h3>
                <ul className="flex flex-col gap-1.5 mt-1">
                  {item.bullets.map((bullet, bi) => (
                    <li
                      key={bi}
                      className="text-white/50 text-sm leading-relaxed flex items-center gap-2"
                    >
                      <span className="w-1 h-1 rounded-full bg-white/25 shrink-0" />
                      {bullet}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
